import { useItemSet } from '@exam-management/common/item-set-provider';
import { QuestionGroupStatus } from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { EDITABLE_ITEM_SET_QUESTION_DETAILS_STATUSES } from '@exam-management/qcontent-management';
import { useNavigate } from '@exam-management/router';
import {
  DeleteOutlineIcon,
  EditOutlinedIcon,
  MoreMenuIconButton,
  type MoreMenuIconButtonOption,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useLogger } from '@shared/logger';
import { usePermission } from '@shared/permissions';
import { useMemo } from 'react';

import { ItemSetSubQuestionDeletionModalLogic } from '../../containers';

interface BaseMoreMenuOptions extends MoreMenuIconButtonOption {
  status: QuestionGroupStatus[];
  requirePermissions: string[];
}

interface ItemSetSubQuestionMoreMenuProps {
  subQuestionId: string;
  structureId: string;
}

export default function ItemSetSubQuestionMoreMenu(
  props: Readonly<ItemSetSubQuestionMoreMenuProps>,
) {
  const { subQuestionId, structureId } = props;

  const { permissions } = usePermission();
  const { itemSet } = useItemSet();

  const navigate = useNavigate();
  const logger = useLogger('ItemSetSubQuestionMoreMenu');

  const [isDeleteModalOpen, deleteModalHandlers] = useDisclosure(false);
  const { open: openDeleteModal, close: closeDeleteModal } = deleteModalHandlers;

  const baseMoreMenuOptions = useMemo<BaseMoreMenuOptions[]>(
    () => [
      {
        icon: <EditOutlinedIcon />,
        label: 'Edit',
        sx: (theme) => ({ color: theme.palette.text.secondary }),
        status: EDITABLE_ITEM_SET_QUESTION_DETAILS_STATUSES,
        requirePermissions: [APP_PERMISSIONS.QBANK.QUESTION.UPDATE],
        onClick: () => {
          navigate('/qbank-structures/:structureId/qcontents/item-set/:questionId/:subQuestionId', {
            params: {
              structureId,
              questionId: itemSet.id,
              subQuestionId,
            },
          });
        },
      },
      // TODO: Uncomment when the duplicate functionality is ready
      // {
      //   icon: <ContentCopyRoundedIcon />,
      //   label: 'Duplicate',
      //   sx: (theme) => ({ color: theme.palette.text.secondary }),
      //   status: [QuestionGroupStatus.Draft],
      //   requirePermissions: [APP_PERMISSIONS.QBANK.QUESTION.CREATE],
      //   onClick: () => {
      //     // TODO: Handle duplicate action
      //     logger.info('Duplicate action clicked with id:' + subQuestionId);
      //   },
      // },
      {
        icon: <DeleteOutlineIcon />,
        label: 'Delete',
        sx: (theme) => ({ color: theme.palette.error.main }),
        status: [QuestionGroupStatus.Draft],
        requirePermissions: [APP_PERMISSIONS.QBANK.QUESTION.UPDATE],
        onClick: openDeleteModal,
      },
    ],
    [navigate, logger],
  );

  const moreMenuOptions = useMemo(
    () =>
      baseMoreMenuOptions
        .filter(
          (option) =>
            option.status.includes(itemSet.status) &&
            option.requirePermissions.every((reqPermission) => permissions.includes(reqPermission)),
        )
        .map(({ icon, label, sx, onClick }) => ({
          icon,
          label,
          sx,
          onClick,
        })),
    [itemSet.status, permissions, baseMoreMenuOptions],
  );

  if (moreMenuOptions.length === 0) {
    return null;
  }

  return (
    <>
      <MoreMenuIconButton
        slotProps={{
          iconButton: { sx: (theme) => ({ color: theme.palette.text.secondary }) },
        }}
        options={moreMenuOptions}
      />
      <ItemSetSubQuestionDeletionModalLogic
        open={isDeleteModalOpen}
        onClose={closeDeleteModal}
        subQuestionId={subQuestionId}
      />
    </>
  );
}
